import * as Model from "../../model"
import * as ProspectiveDomain from "./prospective-domain"
import { isOrder } from "./chapter-domain"

/**
 * Calcule pour l'année `year`, les dépenses réelles de fonctionnement.
 */
export function calculateRealOperationExpense(
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	year: number
): number {
	return calculateRealOperationBudget(
		localAuthority, scenario, ppi, loanDecompositions, year, false)
}

/**
 * Calcule pour l'année `year`, les recettes réelles de fonctionnement.
 */
export function calculateRealOperationReceipts(
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	year: number
): number {
	return calculateRealOperationBudget(
		localAuthority, scenario, ppi, loanDecompositions, year, true)
}

/**
 * Compte le nombre de scénarii établis pour l'année passée en paramètre.
 * @param scenarii
 * @param year
 */
export function count(scenarii: Model.Scenario[], year: number): number {
	return scenarii.filter(s => year === s.year).length
}

/**
 * Filtre la liste de scénarii passés en paramètres par l'année passée en
 * paramètre.
 * @param scenarii
 * @param year
 */
export function filterScenariiByYear(
	scenarii: Model.Scenario[],
	year: number
): Model.Scenario[] {
	return scenarii.filter(s => year === s.year)
}

/**
 * Pondère le montant passé en paramètre
 *
 * - Si un compte de scénario correspondant au compte passé en paramètre existe,
 * le montant retourné est le montant initial pondéré avec la valeur du compte.
 * - Si un chapitre de scénario correspondant au chapitre passé en paramètre
 * existe, le montant retourné est le montant initial pondéré avec la valeur du
 * chapitre.
 * - Sinon le montant retourné est le montant initial.
 *
 * @param rawAmount
 * @param scenario
 * @param account
 * @param chapter
 * @param receipts
 */
export function weightAmount(
	rawAmount: number,
	scenario: Model.Scenario,
	account: Model.Account,
	chapter: Model.Chapter,
	receipts: boolean
): number {
	const scenarioAccount = findScenarioAccount(
		scenario.accounts, account, receipts)

	if (scenarioAccount) return rawAmount * (1 + scenarioAccount.amount / 100)

	const scenarioChapter = findScenarioChapter(
		scenario.chapters, chapter, receipts)

	if (scenarioChapter) return rawAmount * (1 + scenarioChapter.amount / 100)

	return rawAmount
}

/**
 * Trouve le compte de scénario correspondant au compte et au budget passés en
 * paramètres.
 * @param scenarioAccounts
 * @param account
 * @param receipts
 */
export function findScenarioAccount(
	scenarioAccounts: Model.ScenarioAccount[],
	account: Model.Account,
	receipts: boolean
): Model.ScenarioAccount | null {
	return scenarioAccounts.find(sA => account && account.id === sA.account_id
		&& receipts === sA.receipts) || null
}

export function findScenarioAccountThemeByServices(
	scenarioAccountThemes: Model.ScenarioAccountTheme[],
	service: Model.Service,
	account: Model.Account
): Model.ScenarioAccountTheme | null {
	return scenarioAccountThemes.find(t => t.account_id == account.id && t.theme_id == service.id.toString())
}

/**
 * Trouve le chapitre de scénario correspondant au chapitre et au budget passés
 * en paramètres.
 * @param scenarioChapters
 * @param chapter
 * @param receipts
 */
export function findScenarioChapter(
	scenarioChapters: Model.ScenarioChapter[],
	chapter: Model.Chapter,
	receipts: boolean
): Model.ScenarioChapter | null {
	return scenarioChapters.find(sC => chapter && chapter.id === sC.chapter_id
		&& receipts === sC.receipts) || null
}

/**
 * Trouve dans la liste passée en paramètre, l'année de scénario correspondant à
 * l'année passée en paramètre.
 * @param scenarizedYears
 * @param year
 */
export function findScenarioYear(
	scenarizedYears: Model.ScenarioYear[],
	year: number
): Model.ScenarioYear | null {
	if (!scenarizedYears) return null;
	return scenarizedYears.find(sY => sY.year === year) || null
}

/**
 * Trouve un compte d'année de scénario par son identifiant parmi la liste
 * passée en paramètre.
 * @param scenarizedYear
 * @param laca
 */
export function findScenarioYearAccountByAccountId(
	scenarioYearAccounts: Model.ScenarioYearAccount[],
	accountId: number,
	receipts: boolean
): Model.ScenarioYearAccount | null {
	return scenarioYearAccounts.find(sya => accountId === sya.account_id
		&& receipts === sya.receipts) || null
}

/**
 * Trouve parmi les scénarii passés en paramètre le scénario validé pour l'année
 * passée en paramètre
 * @param scenarii
 * @param year
 */
export function findValidatedScenario(
	scenarii: Model.Scenario[],
	year: number
): Model.Scenario | null {
	return scenarii.find(scenario => scenario.validated && year === scenario.year)
	|| null
}

/**
 * Trouve le compte du scénario passé en paramètre attaché au laca passé en
 * paramètre.
 * @param laca
 * @param scenario
 */
export function getScenarizedAccountFromLaca(
	laca: Model.LocalAuthorityChapterAccount,
	scenario: Model.Scenario
): Model.ScenarioAccount | null {
	return (scenario.accounts && scenario.accounts.find(sa =>
		laca.account.id === sa.account_id
	)) || null
}

export function getScenarizedChapterFromLaca(
	laca: Model.LocalAuthorityChapterAccount,
	scenario: Model.Scenario
): Model.ScenarioChapter | null {
	return (scenario.chapters && scenario.chapters.find(sc =>
		laca.chapter && laca.chapter.id === sc.chapter_id
	)) || null
}

export function getScenarizedServices(
	scenario: Model.Scenario,
	service: Model.Service
): Model.ScenarioAccountTheme[] {
	return (scenario.themes && scenario.themes.filter(st =>
		service.slug === st.theme_slug
	)) || []
}

/**
 * Vérifie si `scenario` a une année de scénario à N-1.
 *
 * Si les années de scénario ne sont pas définie ou que la liste est vide, la
 * méthode retourne `false`.
 *
 * @param scenario
 */
export function hasPreviousScenarioYear(scenario: Model.Scenario): boolean {
	if (!scenario.years || !scenario.years.length) return false
	return scenario.years.some(sy => sy.year === scenario.year - 1)
}

export function weightObservationWithAccount(
	observation: Model.Observation,
	scenarizedAccount: Model.ScenarioAccount
): number {
	if (!(observation && scenarizedAccount)) return 0
	return observation.amount * (1 + scenarizedAccount.amount / 100)
}

export function weightObservationWithChapter(
	observation: Model.Observation,
	scenarizedChapter: Model.ScenarioChapter
): number {
	if (!(observation && scenarizedChapter)) return 0
	return observation.amount * (1 + scenarizedChapter.amount / 100)
}

/**
 * Retourne le montant pondéré de l'observation avec la valeur du compte de
 * l'année scénarisée si elle est en pourcentage (percentile == true) sinon
 * retourne la valeur du compte de l'année scénarisée.
 * @param observation
 * @param scenarizedYearAccount
 */
export function weightObservationWithYearAccount(
	observation: Model.Observation,
	scenarizedYearAccount: Model.ScenarioYearAccount
): number {
	// si ni le constat, ni le compte de l'année scénarisée n'existent
	// retourner 0
	if (!(observation && scenarizedYearAccount)) return 0

	// sinon si seul le compte de l'année scénarisée n'existe pas
	// retourner le montant du constant
	if (!scenarizedYearAccount) return observation.amount

	// sinon si le compte de l'année scénarisée est en pourcentage
	// pondérer le montant du constat
	// sinon retourner le montant du compte de l'année scénarisée
	return scenarizedYearAccount.percentile
		? observation.amount * (1 + scenarizedYearAccount.amount / 100)
		: scenarizedYearAccount.amount
}

/**
 * Vérifie si tous les thèmes du scénario sont validés.
 * @param scenario
 */
export function hasAllThemesValidated(
	scenario: Model.Scenario,
	services: Model.Service[]
): boolean {
	return scenario
	&& scenario.validated_themes.length === services.length
	&& scenario.validated_themes.every(st => st.validated)
}

function calculateRealOperationBudget(
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	year: number,
	receipts: boolean
): number {
	return localAuthority.accounts.reduce((sum, laca) => {
		if (!laca.account.operation) return sum
		if (receipts && !laca.account.receipts) return sum
		if (!receipts && !laca.account.expense) return sum
		if (isOrder(laca.chapter, receipts || undefined, !receipts || undefined))
			return sum

		return sum + ProspectiveDomain.findAccountAmount(
			localAuthority,
			laca.account,
			laca.chapter,
			scenario,
			ppi,
			loanDecompositions,
			receipts,
			year)
	}, 0)
}



// WEBPACK FOOTER //
// ./src/domain/sfp/scenario-domain.ts